import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/Toast';
import { C, ACCENT } from '../../theme';
import { Activity, ChevronRight, CheckCircle, ArrowLeft, ChevronDown, ChevronUp, RefreshCw, AlertTriangle, Pencil } from 'lucide-react';

// ── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'fmcg',    label: 'FMCG' },
  { value: 'telco',   label: 'Telco' },
  { value: 'banking', label: 'Banking' },
  { value: 'retail',  label: 'Retail' },
  { value: 'pharma',  label: 'Pharma' },
];

const MOMENTS = [
  { value: 'new_launch', label: 'New launch',         desc: 'Entering the market or launching a new product' },
  { value: 'promo',      label: 'Promotion',          desc: 'Running a time-limited campaign or activation' },
  { value: 'defensive',  label: 'Defensive',          desc: 'Protecting existing position against competitive pressure' },
  { value: 'bau',        label: 'Business as usual',  desc: 'Maintaining current presence, no major campaign' },
];

const TRENDS = [
  { value: 'declining', label: 'Declining' },
  { value: 'stable',    label: 'Stable' },
  { value: 'growing',   label: 'Growing' },
];

const DIST_CATS = new Set(['fmcg', 'retail', 'pharma']);

const STEPS = ['A', 'B', 'C', 'D'];

const STEP_SUBTITLE = {
  A: "Let's start with the basics",
  B: 'These unlock more specific recommendations',
  C: 'Competitive context, if you have it',
  D: 'The last few, for the most complete picture',
};

const STEP_NEXT = { A: 'B', B: 'C', C: 'D' };

const PCT_FIELDS = ['aided_awareness','unaided_awareness','preference','trial','consumption',
  'loyalty','market_share','distribution','competitor_share','competitor_awareness'];

const FIELD_LABEL = {
  business_moment:      'Business Moment',
  aided_awareness:      'Aided Awareness',
  unaided_awareness:    'Unaided Awareness',
  preference:           'Brand Preference',
  market_share:         'Market Share',
  sales_trend:          'Sales Trend',
  category_trend:       'Category Trend',
  competitor_share:     'Competitor Market Share',
  competitor_awareness: 'Competitor Aided Awareness',
  loyalty:              'Loyalty / Repeat Purchase',
  trial:                'Trial',
  consumption:          'Consumption',
  distribution:         'Distribution',
  annual_budget:        'Annual Media Budget',
};

// ── Styles ───────────────────────────────────────────────────────────────────

const card = {
  background: 'rgba(255,255,255,0.88)',
  backdropFilter: 'blur(12px)',
  border: '1px solid #dce8f0',
  borderRadius: 14,
  padding: '32px 36px',
  maxWidth: 560,
  margin: '0 auto',
  boxShadow: '0 4px 24px rgba(80,120,160,0.10)',
};

const muted  = { fontSize: 12, color: '#8aa0b8', marginTop: 5 };
const bmHint = { fontSize: 12, color: ACCENT, marginTop: 5 };
const backBtn = {
  background: 'none', border: 'none', color: '#8aa0b8', cursor: 'pointer',
  fontSize: 12, padding: 0, display: 'flex', alignItems: 'center', gap: 4,
};

const briefCard = {
  background: 'rgba(255,255,255,0.92)',
  border: '1px solid #dce8f0',
  borderRadius: 14,
  padding: '28px 32px',
  maxWidth: 720,
  margin: '0 auto',
  boxShadow: '0 4px 24px rgba(80,120,160,0.08)',
};

const researchCard = {
  ...briefCard,
  background: 'rgba(232,244,252,0.92)',
  border: '1px solid #c8dcea',
};

// Phase 3 — which wizard step a flaggable field lives on. Only fields that can
// ever appear in a suggestion (see backend _ALLOWED_SUGGESTION_FIELDS) need an
// entry here; anything else falls back to step A in handleEditSuggestion.
const SUGGESTION_FIELD_STEP = {
  business_moment: 'A',
  sales_trend: 'B',
  category_trend: 'B',
  competitor_share: 'C',
  competitor_awareness: 'C',
  competitors: 'C',
};

const SUGGESTION_FIELD_LABEL = {
  business_moment: 'Business Moment',
  sales_trend: 'Sales Trend',
  category_trend: 'Category Trend',
  competitor_share: 'Competitor Share',
  competitor_awareness: 'Competitor Awareness',
  competitors: 'Competitor',
};

const textBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: ACCENT, fontSize: 12, padding: 0,
};

// ── Brief helpers ─────────────────────────────────────────────────────────────

const NARRATIVE_SECTIONS = ['SITUATION', 'STRATEGIC RECOMMENDATION', 'MEDIA DIRECTION'];

function parseNarrative(text) {
  if (!text) return [];
  const result = [];
  let remaining = text;
  for (let i = 0; i < NARRATIVE_SECTIONS.length; i++) {
    const h = NARRATIVE_SECTIONS[i];
    const next = NARRATIVE_SECTIONS[i + 1];
    const hRx = new RegExp(`^${h.replace(/ /g, '\\s+')}\\s*$`, 'm');
    const hm = hRx.exec(remaining);
    if (!hm) continue;
    const after = remaining.slice(hm.index + hm[0].length);
    let content;
    if (next) {
      const nRx = new RegExp(`^${next.replace(/ /g, '\\s+')}\\s*$`, 'm');
      const nm = nRx.exec(after);
      if (nm) { content = after.slice(0, nm.index).trim(); remaining = after.slice(nm.index); }
      else     { content = after.trim(); remaining = ''; }
    } else {
      content = after.trim();
    }
    result.push({ header: h, content });
  }
  return result.length > 0 ? result : [{ header: null, content: text.trim() }];
}

const TYPE_LABEL = {
  category_trend: 'Category Trends',
  competitor:     'Competitive Landscape',
  brand_signal:   'Consumer Signals',
  market_context: 'Media Landscape',
};
const TYPE_ORDER = ['category_trend', 'competitor', 'brand_signal', 'market_context'];

function groupFindings(findings) {
  if (!findings || findings.length === 0) return { grouped: false, items: [] };
  const types = new Set(findings.map(f => f.type));
  if (types.size > 1 && findings.length >= 4) {
    const byType = {};
    for (const f of findings) { if (!byType[f.type]) byType[f.type] = []; byType[f.type].push(f); }
    return {
      grouped: true,
      groups: TYPE_ORDER.filter(t => byType[t]).map(t => ({ type: t, label: TYPE_LABEL[t] || t, findings: byType[t] })),
    };
  }
  return { grouped: false, items: findings };
}

const KPI_LABEL = {
  aided_awareness: 'Aided Awareness', unaided_awareness: 'Unaided Awareness',
  reach: 'Reach', frequency: 'Frequency', preference: 'Brand Preference',
  market_share: 'Market Share', trial: 'Trial Rate', consumption: 'Consumption',
  loyalty: 'Loyalty', ctr: 'CTR', conversions: 'Conversions', distribution: 'Distribution',
};

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function emptyCompetitor() {
  return { name: '', awareness: '', share: '', consumption: '' };
}

function emptyPromoContext() {
  return {
    is_repetitive: '', years_running: '', mechanic_changed: '', mechanic_change_note: '',
    prior_year_participation_rate: '', current_participation_rate: '', participation_decline_pct: '',
    media_weight_vs_prior: '', expected_participation_rate: '',
    campaign_start_date: '', campaign_end_date: '',
  };
}

function emptyForm() {
  return {
    business_moment: '', aided_awareness: '', unaided_awareness: '', preference: '',
    trial: '', consumption: '', loyalty: '', market_share: '', distribution: '',
    sales_trend: '', category_trend: '', competitor_share: '', competitor_awareness: '',
    annual_budget: '', market: '',
    competitors: [emptyCompetitor()],
    promo_context: emptyPromoContext(),
    market_context_notes: '',
  };
}

function setPromoField(setForm, field, value) {
  setForm(p => ({ ...p, promo_context: { ...(p.promo_context || emptyPromoContext()), [field]: value } }));
}

function getBmHint(field, bm, category) {
  const cat = category ? (category[0].toUpperCase() + category.slice(1)) : '';
  if (field === 'aided_awareness') {
    const nb = bm['aided_awareness_new_brand'], ld = bm['aided_awareness_leader'];
    if (!nb && !ld) return null;
    const parts = [];
    if (nb) parts.push(`New brand: ${nb.value_low}–${nb.value_high}%`);
    if (ld) parts.push(`Leader: ${ld.value_low}–${ld.value_high}%`);
    return `Typical (${cat}): ${parts.join(' · ')}`;
  }
  if (field === 'unaided_awareness') {
    const b = bm['unaided_awareness_top_of_mind'];
    return b ? `Top-of-mind threshold (${cat}): ${b.value_low}–${b.value_high}%` : null;
  }
  if (field === 'market_share') {
    const ld = bm['market_share_leader'], av = bm['market_share_average'];
    if (!ld && !av) return null;
    const parts = [];
    if (ld) parts.push(`Leader: ${ld.value_low}–${ld.value_high}%`);
    if (av) parts.push(`Mid-tier: ${av.value_low}–${av.value_high}%`);
    return `Typical (${cat}): ${parts.join(' · ')}`;
  }
  if (field === 'distribution') {
    const b = bm['distribution_healthy_min'];
    return b ? `Healthy minimum (${cat}): ${b.value_low}%+` : null;
  }
  return null;
}

function buildPayload(form) {
  const out = {};
  // competitor_share/competitor_awareness are derived from competitors[0] below — skip here
  PCT_FIELDS
    .filter(f => f !== 'competitor_share' && f !== 'competitor_awareness')
    .forEach(f => { if (form[f] !== '') out[f] = parseFloat(form[f]); });
  if (form.business_moment) out.business_moment = form.business_moment;
  if (form.sales_trend)     out.sales_trend = form.sales_trend;
  if (form.category_trend)  out.category_trend = form.category_trend;
  if (form.annual_budget !== '') out.annual_budget = parseFloat(form.annual_budget);
  if (form.market)          out.market = form.market;
  if (form.market_context_notes && form.market_context_notes.trim()) out.market_context_notes = form.market_context_notes.trim();
  // Back-fill legacy scalar fields from main competitor row (rule engine reads these directly)
  const mainComp = (form.competitors || [])[0];
  if (mainComp) {
    if (mainComp.share !== '')     out.competitor_share = parseFloat(mainComp.share);
    if (mainComp.awareness !== '') out.competitor_awareness = parseFloat(mainComp.awareness);
  }
  const validCompetitors = (form.competitors || [])
    .filter(c => c.name && c.name.trim())
    .slice(0, 3)
    .map(c => {
      const entry = { name: c.name.trim() };
      if (c.awareness !== '') entry.awareness = parseFloat(c.awareness);
      if (c.share !== '')     entry.share = parseFloat(c.share);
      if (c.consumption !== '') entry.consumption = parseFloat(c.consumption);
      return entry;
    });
  if (validCompetitors.length > 0) out.competitors = validCompetitors;

  // promo_context: only send when the required fields (is_repetitive + both dates) are set
  const pc = form.promo_context;
  if (form.business_moment === 'promo' && pc
      && (pc.is_repetitive === 'true' || pc.is_repetitive === 'false')
      && pc.campaign_start_date && pc.campaign_end_date) {
    const isRepetitive = pc.is_repetitive === 'true';
    const outPc = {
      is_repetitive: isRepetitive,
      campaign_start_date: pc.campaign_start_date,
      campaign_end_date: pc.campaign_end_date,
    };
    if (isRepetitive) {
      if (pc.years_running !== '') outPc.years_running = parseInt(pc.years_running, 10);
      if (pc.mechanic_changed === 'true' || pc.mechanic_changed === 'false') {
        outPc.mechanic_changed = pc.mechanic_changed === 'true';
        if (outPc.mechanic_changed && pc.mechanic_change_note) outPc.mechanic_change_note = pc.mechanic_change_note;
      }
      if (pc.prior_year_participation_rate !== '') outPc.prior_year_participation_rate = parseFloat(pc.prior_year_participation_rate);
      if (pc.current_participation_rate !== '') outPc.current_participation_rate = parseFloat(pc.current_participation_rate);
      if (pc.participation_decline_pct !== '') outPc.participation_decline_pct = parseFloat(pc.participation_decline_pct);
      if (pc.media_weight_vs_prior) outPc.media_weight_vs_prior = pc.media_weight_vs_prior;
    } else {
      if (pc.expected_participation_rate !== '') outPc.expected_participation_rate = parseFloat(pc.expected_participation_rate);
      if (pc.current_participation_rate !== '') outPc.current_participation_rate = parseFloat(pc.current_participation_rate);
    }
    out.promo_context = outPc;
  }
  return out;
}

function populateForm(bh) {
  const f = emptyForm();
  PCT_FIELDS.forEach(field => {
    if (bh[field] != null) f[field] = String(bh[field]);
  });
  if (bh.business_moment) f.business_moment = bh.business_moment;
  if (bh.sales_trend)     f.sales_trend = bh.sales_trend;
  if (bh.category_trend)  f.category_trend = bh.category_trend;
  if (bh.annual_budget != null) f.annual_budget = String(bh.annual_budget);
  if (bh.market)          f.market = bh.market;
  if (bh.market_context_notes) f.market_context_notes = bh.market_context_notes;
  if (bh.competitors && Array.isArray(bh.competitors) && bh.competitors.length > 0) {
    f.competitors = bh.competitors.map(c => ({
      name: c.name || '',
      awareness: c.awareness != null ? String(c.awareness) : '',
      share: c.share != null ? String(c.share) : '',
      consumption: c.consumption != null ? String(c.consumption) : '',
    }));
  } else {
    // Old snapshot: populate row 0's share/awareness from legacy scalar fields
    if (bh.competitor_share != null)     f.competitors[0].share = String(bh.competitor_share);
    if (bh.competitor_awareness != null) f.competitors[0].awareness = String(bh.competitor_awareness);
  }
  if (bh.promo_context && typeof bh.promo_context === 'object') {
    const pc = bh.promo_context;
    f.promo_context = {
      is_repetitive: pc.is_repetitive === true ? 'true' : pc.is_repetitive === false ? 'false' : '',
      years_running: pc.years_running != null ? String(pc.years_running) : '',
      mechanic_changed: pc.mechanic_changed === true ? 'true' : pc.mechanic_changed === false ? 'false' : '',
      mechanic_change_note: pc.mechanic_change_note || '',
      prior_year_participation_rate: pc.prior_year_participation_rate != null ? String(pc.prior_year_participation_rate) : '',
      current_participation_rate: pc.current_participation_rate != null ? String(pc.current_participation_rate) : '',
      participation_decline_pct: pc.participation_decline_pct != null ? String(pc.participation_decline_pct) : '',
      media_weight_vs_prior: pc.media_weight_vs_prior || '',
      expected_participation_rate: pc.expected_participation_rate != null ? String(pc.expected_participation_rate) : '',
      campaign_start_date: pc.campaign_start_date || '',
      campaign_end_date: pc.campaign_end_date || '',
    };
  }
  return f;
}

function catLabel(cat) {
  return CATEGORIES.find(c => c.value === cat)?.label || cat || '';
}

// ── Generate Plan from Brief helpers ──────────────────────────────────────────

function getRuleType(ruleId) {
  if (!ruleId) return null;
  const ids = ruleId.split('+').map(r => r.trim());
  if (ids.includes('R20')) return 'promo_response';
  if (ids.includes('R21')) return 'promo_new';
  if (ids.some(r => ['R5', 'R16', 'R4', 'R13'].includes(r))) return 'two_sequence';
  if (ids.includes('R8') && ids.includes('R1')) return 'launch';
  if (ids.some(r => ['R11', 'R12'].includes(r))) return 'burst';
  if (ids.includes('R14')) return 'seasonal';
  return 'continuous';
}

function normalizeObjective(planiqObjective) {
  const map = { Awareness: 'awareness', Traffic: 'traffic', Conversion: 'conversion' };
  return map[planiqObjective] || 'awareness';
}

const FLIGHT_PLANS = {
  continuous: {
    1: { label: 'Stay visible all year', flights: [{ weeks: 52 }] },
    2: { label: 'Concentrated push — 6 months', flights: [{ weeks: 4 }, { weeks: 4, gapWeeks: 4 }, { weeks: 4, gapWeeks: 4 }] },
    3: { label: 'Validate the approach first', flights: [{ weeks: 6 }] },
  },
  burst: {
    1: { label: 'Full activation window', flights: [{ weeks: 6 }] },
    2: { label: 'Standard burst', flights: [{ weeks: 4 }] },
    3: { label: 'Short test', flights: [{ weeks: 2 }] },
  },
  two_sequence: {
    1: { label: 'Immediate response + sustained presence', flights: [{ weeks: 8, budgetShare: 0.6 }, { weeks: 20, budgetShare: 0.4 }] },
    2: { label: 'Burst + follow-up', flights: [{ weeks: 8, budgetShare: 0.7 }, { weeks: 12, budgetShare: 0.3 }] },
    3: { label: 'Test the burst first', flights: [{ weeks: 8, budgetShare: 1.0 }] },
  },
  launch: {
    1: { label: 'Full launch sequence', flights: [{ weeks: 4, budgetShare: 0.4, objOverride: 'awareness' }, { weeks: 8, budgetShare: 0.6, objOverride: 'traffic' }] },
    2: { label: 'Compact launch', flights: [{ weeks: 4, budgetShare: 0.5, objOverride: 'awareness' }, { weeks: 4, budgetShare: 0.5, objOverride: 'traffic' }] },
    3: { label: 'Awareness phase only', flights: [{ weeks: 4, budgetShare: 1.0, objOverride: 'awareness' }] },
  },
  seasonal: {
    1: { label: 'Full seasonal window', flights: [{ weeks: 6 }] },
    2: { label: 'Core window', flights: [{ weeks: 4 }] },
    3: { label: 'Peak only', flights: [{ weeks: 2 }] },
  },
};
// R21 (new promo) is informational, not urgent — normal planning cadence applies, defaulted
// to the same burst/activation shape as R11. promo_response (R20) intentionally has no entry
// here: it uses a custom single-flight recovery card, not the 3-option selector. When R20's
// campaign has already ended, the generic `FLIGHT_PLANS[ruleType] || FLIGHT_PLANS.continuous`
// fallback picks up 'continuous' automatically since 'promo_response' has no key.
FLIGHT_PLANS.promo_new = FLIGHT_PLANS.burst;

function calcRecommendedBudget(objective, totalWeeks) {
  // PlanIQ's budget/reach calculations are currently calibrated to Moldova only.
  // Multi-market support (per-brand population/benchmark data) is a planned future
  // feature, not yet implemented. Do not remove this comment when refactoring — it
  // documents a known scope limitation, not an oversight.
  const MOLDOVA_UNIVERSE = 1887226;
  const AVG_CPMS = { awareness: 0.75, traffic: 0.25, views: 1.50, conversion: 0.30 };
  const MIN_TARGETS = { awareness: 0.50, traffic: 20000, views: 200000, conversion: 5000 };
  const MARKET_CAP = 100000;
  const cpm = AVG_CPMS[objective] || 0.75;
  let budget;
  if (objective === 'awareness') {
    const targetUsers = MOLDOVA_UNIVERSE * MIN_TARGETS.awareness;
    const impressions = targetUsers * 4 * totalWeeks;
    budget = (impressions / 1000) * cpm;
  } else if (objective === 'traffic') {
    budget = MIN_TARGETS.traffic * cpm * (totalWeeks / 4);
  } else if (objective === 'views') {
    budget = (MIN_TARGETS.views / 1000) * cpm * (totalWeeks / 4);
  } else {
    budget = MIN_TARGETS.conversion * cpm * (totalWeeks / 4);
  }
  const annualEquiv = budget * (52 / totalWeeks);
  if (annualEquiv > MARKET_CAP) budget = MARKET_CAP * (totalWeeks / 52);
  return Math.round(budget / 1000) * 1000;
}

// Day-based (not ISO-week-based) diff — safe across a year boundary, unlike subtracting
// two dateToWeek() results, which resets to 1 at each year's week 1.
function weeksBetween(fromISO, toISO) {
  const [fy, fm, fd] = fromISO.split('-').map(Number);
  const [ty, tm, td] = toISO.split('-').map(Number);
  const from = _utcDate(fy, fm - 1, fd);
  const to = _utcDate(ty, tm - 1, td);
  return Math.ceil((to - from) / 604800000);
}

// R20 recovery flight budget — used only when no annual_budget override is present.
// Deliberately does NOT reuse calcRecommendedBudget('conversion', ...): that formula's flat
// MIN_TARGETS.conversion=5000 assumes a generic mid-size conversion campaign, which badly
// undershoots a mass-participation retail promo on a brand with near-universal reach (a
// dominant retailer's promo recovery needs to reach a large share of the population to move
// participation, not chase 5,000 discrete conversions). Instead scales a reach target by how
// large/dominant the brand already is — aided_awareness x distribution as a proxy for how
// much of the population this brand can plausibly move. Falls back to a conservative 25%
// reach target when awareness/distribution aren't known. The 0.70 cap is intentional, not a
// magnitude dial: it's correcting for unrealistic raw reach products (e.g. 99%x99%=98%), not
// something to loosen for a target number.
// CPM uses RECOVERY_CPM, not the generic conversion CPM: a promo recovery push is fundamentally
// reach media (broad video/display) carrying a "participate now" message, not narrow
// performance/retargeting inventory — so it prices closer to the awareness CPM than the
// conversion CPM. Rough heuristic pending real campaign performance data (actual media cost
// per participation-rate-point) to calibrate against — refine once that data exists.
function calcRecoveryBudget(weeksRemaining, annualBudgetOverride, aidedAwareness, distribution) {
  if (annualBudgetOverride != null) return Math.round(annualBudgetOverride);

  // PlanIQ's budget/reach calculations are currently calibrated to Moldova only.
  // Multi-market support (per-brand population/benchmark data) is a planned future
  // feature, not yet implemented. Do not remove this comment when refactoring — it
  // documents a known scope limitation, not an oversight.
  const MOLDOVA_UNIVERSE = 1887226;
  const RECOVERY_CPM = 0.65; // reach-style media (broad video/display) carrying a conversion message —
                              // closer to AVG_CPMS.awareness (0.75) than AVG_CPMS.conversion (0.30),
                              // since a promo recovery push needs broad reach inventory, not narrow retargeting

  const scaleFactor = (aidedAwareness != null && distribution != null)
    ? Math.min(0.70, (aidedAwareness / 100) * (distribution / 100))
    : 0.25;

  const targetUsers = MOLDOVA_UNIVERSE * scaleFactor;
  const avgFreq = 5; // urgent recovery — higher frequency than standard awareness (4)
  const impressions = targetUsers * avgFreq * weeksRemaining;
  const budget = (impressions / 1000) * RECOVERY_CPM;

  return Math.round(budget / 1000) * 1000;
}

function optionTotals(option, objective, annualBudgetOverride) {
  const totalWeeks = option.flights.reduce((s, f) => s + f.weeks + (f.gapWeeks || 0), 0);
  const totalBudget = annualBudgetOverride != null
    ? Math.round(annualBudgetOverride * (totalWeeks / 52))
    : calcRecommendedBudget(objective, totalWeeks);
  return { totalWeeks, totalBudget };
}

function flightSummary(option, objective) {
  const flights = option.flights;
  const totalWeeks = flights.reduce((s, f) => s + f.weeks + (f.gapWeeks || 0), 0);
  const months = Math.round(totalWeeks / 4.345);
  const durationLabel = totalWeeks >= 8 ? `${months} month${months === 1 ? '' : 's'}` : `${totalWeeks} week${totalWeeks === 1 ? '' : 's'}`;
  const hasOverrides = flights.some(f => f.objOverride);
  let structure;
  if (flights.length === 1) {
    structure = `1 continuous ${flights[0].objOverride || objective} flight`;
  } else if (hasOverrides) {
    structure = flights.map(f => `${f.weeks}wk ${f.objOverride || objective}`).join(' → ');
  } else if (flights.every(f => f.weeks === flights[0].weeks)) {
    structure = `${flights.length} ${objective} flights × ${flights[0].weeks} weeks`;
  } else {
    structure = `${flights.length} ${objective} flights`;
  }
  return `${durationLabel} · ${structure}`;
}

// ISO week arithmetic — same algorithm as DigitalPlanningEngine.js's date utils, but year-parameterized
// here (DPE's own copy still hardcodes YEAR=2026 internally for its Flowchart grid — see known limitation
// noted where buildFlightConfigs clamps). Do not reintroduce a hardcoded year in this file.
function _utcDate(y, m, d) { return new Date(Date.UTC(y, m, d)); }
function _utcDow(d) { return (d.getUTCDay() + 6) % 7; } // Mon=0..Sun=6
function isoWeekYear(s) {
  const [y, m, d] = s.split('-').map(Number);
  const dt = _utcDate(y, m - 1, d);
  const thu = new Date(dt); thu.setUTCDate(dt.getUTCDate() + 3 - _utcDow(dt));
  return thu.getUTCFullYear();
}
function dateToWeek(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  const dt = _utcDate(y, m - 1, d);
  const thu = new Date(dt); thu.setUTCDate(dt.getUTCDate() + 3 - _utcDow(dt));
  const w1 = _utcDate(thu.getUTCFullYear(), 0, 4);
  const w1thu = new Date(w1); w1thu.setUTCDate(w1.getUTCDate() + 3 - _utcDow(w1));
  const wn = 1 + Math.round((thu - w1thu) / 604800000);
  return Math.max(1, Math.min(53, wn));
}
function weekStartDate(w, year) {
  const jan4 = _utcDate(year, 0, 4);
  const w1mon = new Date(jan4); w1mon.setUTCDate(jan4.getUTCDate() - _utcDow(jan4));
  const mon = new Date(w1mon); mon.setUTCDate(w1mon.getUTCDate() + (w - 1) * 7);
  return mon.toISOString().slice(0, 10);
}
function weekEndDate(w, year) {
  const s = weekStartDate(w, year);
  const [y, m, d] = s.split('-').map(Number);
  return _utcDate(y, m - 1, d + 6).toISOString().slice(0, 10);
}
function firstMondayOfNextMonth() {
  const now = new Date();
  const first = _utcDate(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
  const addDays = (7 - _utcDow(first)) % 7;
  const mon = new Date(first); mon.setUTCDate(first.getUTCDate() + addDays);
  return mon.toISOString().slice(0, 10);
}

// Duration-aware default: long options (half-year+) anchor to the Monday of ISO week 1 of a
// year that can fit the full span; short options default to near-term activation.
// Uses weekStartDate(1, targetYear) rather than a literal "YYYY-01-01" string — Jan 1 does not
// always fall in week 1 of its own calendar year under ISO 8601 (e.g. 2027-01-01 is a Friday,
// so it actually belongs to week 53 of 2026). Snapping through the week helper avoids that trap.
// Runway-based (not month-cutoff-based): Jan 1 of the current year has always already passed
// by the time anyone is using the app, so week 1 of currentYear is only a valid suggestion in
// the (impossible in practice) case that it hasn't happened yet. This makes "which year" fall
// out of a single hasn't-passed check rather than a hardcoded "September onward" heuristic,
// which could suggest a start date already in the past (e.g. "now"=July → suggested Jan 1, same year).
function defaultStartDate(totalWeeks) {
  const now = new Date();
  if (totalWeeks >= 26) {
    const currentYear = now.getUTCFullYear();
    const janThisYear = weekStartDate(1, currentYear);
    const targetYear = (new Date(janThisYear) > now) ? currentYear : currentYear + 1;
    return weekStartDate(1, targetYear);
  }
  return firstMondayOfNextMonth();
}

// Returns { configs, clamped }. `clamped` is kept for backward compatibility with its one
// call site (which shows a toast when true) but is now always false — see below for why.
//
// PRIOR BEHAVIOR (removed 2026-08-24): this used to shorten a flight — down to as little
// as 1 week — whenever startWeek+weeks-1 exceeded a hardcoded 52, on the theory that
// DigitalPlanningEngine's Flowchart tab couldn't display a flight past week 52 of its own
// year. That was true when it was written, but is no longer true on two counts fixed
// earlier today: (1) the Flowchart now supports a real ISO week 53 for years that have one
// (weeksInYear()), and (2) more fundamentally, the Flowchart's year-selector already lets a
// flight span two real years and renders each year's portion correctly when that year is
// selected — a flight is no longer required to fit inside a single calendar year for
// display purposes at all. The clamp's entire premise is gone, and simply raising its
// ceiling from a hardcoded 52 to a dynamic weeksInYear(year) does NOT fix the reported bug
// either: a flight starting in week 53 of a 53-week year (year's OWN last week) with
// weeks=4 would still compute max(1, weeksInYear-53+1)=1 under that approach, identical
// truncation. The only correct fix is to stop trying to fit weeks+dates inside one year's
// week-numbering at all — exactly what calcFlight already does via a date-anchored walk
// (see project_dpe_year_hardcoding_fix memory) rather than a [startWeek,endWeek] range.
// flightEndDate below is now pure calendar-date arithmetic from the real start date — it
// doesn't need to know how many weeks are in any year, because it never assumes a week
// number resets at Dec 31; that's what lets it cross a year boundary correctly for free.
function buildFlightConfigs(option, startDateStr, objective, totalBudget) {
  const totalActiveWeeks = option.flights.reduce((s, f) => s + f.weeks, 0);
  const [sy, sm, sd] = startDateStr.split('-').map(Number);
  let cursor = _utcDate(sy, sm - 1, sd);
  const configs = option.flights.map(f => {
    cursor = new Date(cursor); cursor.setUTCDate(cursor.getUTCDate() + (f.gapWeeks || 0) * 7);
    const cursorStr = cursor.toISOString().slice(0, 10);
    const year = isoWeekYear(cursorStr);
    const startWeek = dateToWeek(cursorStr);
    const flightStartDate = weekStartDate(startWeek, year);

    const weeks = f.weeks;
    const endCursor = new Date(flightStartDate + 'T00:00:00Z');
    endCursor.setUTCDate(endCursor.getUTCDate() + weeks * 7 - 1);
    const flightEndDate = endCursor.toISOString().slice(0, 10);

    const share = f.budgetShare != null ? f.budgetShare : (f.weeks / totalActiveWeeks);
    const budget = Math.max(500, Math.round((totalBudget * share) / 100) * 100);

    cursor = new Date(cursor); cursor.setUTCDate(cursor.getUTCDate() + f.weeks * 7);
    return {
      startDate: flightStartDate, endDate: flightEndDate, startWeek, weeks,
      budget, objective: f.objOverride || objective,
    };
  });
  return { configs, clamped: false };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PageHeader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
      <Activity size={22} color={ACCENT} />
      <h1 style={C.title}>Strategic Diagnostic</h1>
    </div>
  );
}

function StepDots({ current }) {
  const idx = STEPS.indexOf(current);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 24 }}>
      {STEPS.map((s, i) => {
        const done = i < idx, active = s === current;
        return (
          <React.Fragment key={s}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700,
              background: done ? `${ACCENT}20` : active ? ACCENT : 'transparent',
              color: done ? ACCENT : active ? '#fff' : '#8aa0b8',
              border: done ? `1.5px solid ${ACCENT}50` : active ? 'none' : '1.5px solid #c8d8e8',
            }}>
              {done ? <CheckCircle size={13} /> : s}
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1, background: i < idx ? `${ACCENT}40` : '#dce8f0' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function PctInput({ label, field, form, setForm, help, bm, category, nudge }) {
  const val = form[field];
  const hint = val !== '' ? getBmHint(field, bm, category) : null;
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={C.label}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input type="number" min="0" max="100" step="0.1"
          value={val}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
          placeholder="0–100"
          style={{ ...C.input, paddingRight: 32 }} />
        <span style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', color: '#8aa0b8', fontSize: 13, pointerEvents: 'none' }}>%</span>
      </div>
      {help && <div style={muted}>{help}</div>}
      {hint && <div style={bmHint}>{hint}</div>}
      {nudge && <div style={{ fontSize: 12, color: '#4a6a8a', marginTop: 6, padding: '6px 10px', background: `${ACCENT}0d`, borderRadius: 6, borderLeft: `3px solid ${ACCENT}50` }}>{nudge}</div>}
    </div>
  );
}

function SelectField({ label, field, form, setForm, options }) {
  const val = form[field];
  const selected = options.find(o => o.value === val);
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={C.label}>{label}</label>
      <select value={val} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
        style={{ ...C.input, cursor: 'pointer' }}>
        <option value="">— Select —</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {selected?.desc && <div style={muted}>{selected.desc}</div>}
    </div>
  );
}

// ── Promo sub-section inputs (operate on the nested form.promo_context object) ────

function PromoSelect({ label, value, onChange, options, help }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={C.label}>{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ ...C.input, cursor: 'pointer' }}>
        <option value="">— Select —</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {help && <div style={muted}>{help}</div>}
    </div>
  );
}

function PromoPctInput({ label, value, onChange, help }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={C.label}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input type="number" min="0" max="100" step="0.1"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="0–100"
          style={{ ...C.input, paddingRight: 32 }} />
        <span style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', color: '#8aa0b8', fontSize: 13, pointerEvents: 'none' }}>%</span>
      </div>
      {help && <div style={muted}>{help}</div>}
    </div>
  );
}

function PromoNumberInput({ label, value, onChange, help }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={C.label}>{label}</label>
      <input type="number" min="0" step="1" value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={C.input} />
      {help && <div style={muted}>{help}</div>}
    </div>
  );
}

function PromoDateInput({ label, value, onChange, required }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={C.label}>{label}{required && <span style={{ color: '#a02020' }}> *</span>}</label>
      <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} style={C.input} />
    </div>
  );
}

function PromoTextArea({ label, value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={C.label}>{label}</label>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        rows={2} style={{ ...C.input, resize: 'vertical', fontFamily: 'inherit', height: 'auto' }} />
    </div>
  );
}

const promoSectionStyle = {
  marginTop: -8, marginBottom: 20, padding: '16px 16px 4px',
  background: '#f4f8fb', borderRadius: 10, border: '1px solid #dce8f0',
};

// ── Main component ────────────────────────────────────────────────────────────

export default function DiagnosticPage() {
  const { brandId } = useParams();
  const { isManager } = useAuth();
  const { showToast } = useToast();

  const navigate = useNavigate();

  /*
   * State machine — view transitions
   *   pick         → brand_home  (brand selected + category set)
   *   pick         → category    (brand selected, category missing)
   *   category     → brand_home  (after category saved)
   *   brand_home   → A           ("New Diagnostic →" / "New snapshot" from brief)
   *   brand_home   → brief       ("Load" from list)
   *   A/B/C/D      → brand_home  (saveHealth completes OR cancel/back)
   *   brief        → brand_home  (Back)
   *   brief        → A           ("New snapshot")
   *   "done" view removed — wizard save transitions directly to brand_home
   */

  const [view, setView]             = useState(brandId ? 'loading' : 'pick');
  const [brands, setBrands]         = useState([]);
  const [brand, setBrand]           = useState(null);
  const [bm, setBm]                 = useState({});
  const [form, setForm]             = useState(emptyForm);
  const [savedHealth, setSavedHealth] = useState(null);
  // Phase 1 pre-fill research — offered once per fresh wizard entry (see the two
  // setForm(emptyForm()) call sites, both reset this to 'idle'). 'idle' shows the
  // Run/Skip offer, 'loading' shows the researching state, 'done' shows the
  // verified/unverified summary, 'dismissed' hides the block entirely.
  const [prefillStatus, setPrefillStatus]     = useState('idle');
  const [prefillResearch, setPrefillResearch] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [savingCat, setSavingCat]   = useState(false);
  const [catDraft, setCatDraft]     = useState('');
  const [search, setSearch]         = useState('');

  // Brief state
  const [brief, setBrief]                     = useState(null);
  const [briefLoading, setBriefLoading]       = useState(false);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeErr, setNarrativeErr]       = useState(null);
  const [researchLoading, setResearchLoading] = useState(false);
  const [secondaryOpen, setSecondaryOpen]     = useState(false);
  const [history, setHistory]                 = useState([]);
  const [historyLoading, setHistoryLoading]   = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [latestSnapshot, setLatestSnapshot]   = useState(null);

  // Generate Plan from Brief state
  const [selectedOption, setSelectedOption]         = useState(1);
  const [startDate, setStartDate]                   = useState(firstMondayOfNextMonth);
  const [startDateTouched, setStartDateTouched]     = useState(false);
  const [creatingPlan, setCreatingPlan]              = useState(false);

  useEffect(() => {
    if (view !== 'pick') return;
    api.getBrands().then(setBrands).catch(() => {});
  }, [view]);

  useEffect(() => {
    if (!brandId) return;
    api.getBrand(brandId)
      .then(async b => {
        setBrand(b);
        if (!b.category) { setView('category'); return; }
        await loadBenchmarks(b.id);
        setView('brand_home');
      })
      .catch(() => setView('pick'));
  }, [brandId]);

  // Load brief list + latest snapshot whenever entering brand_home
  useEffect(() => {
    if (view !== 'brand_home' || !brand?.id) return;
    setHistoryLoading(true);
    Promise.all([
      api.getStrategicBriefs(brand.id).catch(() => []),
      api.getLatestBrandHealth(brand.id).catch(() => null),
    ])
      .then(([list, snap]) => {
        setHistory(list);
        setLatestSnapshot(snap?.id ? snap : null);
      })
      .finally(() => setHistoryLoading(false));
  }, [view, brand?.id]);

  // Reset "Build your plan" selection whenever a (different) brief is loaded
  useEffect(() => {
    if (!brief?.id) return;
    setSelectedOption(1);
    setStartDateTouched(false);
    const ruleType = getRuleType(brief.primary_diagnosis?.rule_id);
    const opt = (FLIGHT_PLANS[ruleType] || FLIGHT_PLANS.continuous)[1];
    const totalWeeks = opt.flights.reduce((s, f) => s + f.weeks + (f.gapWeeks || 0), 0);
    setStartDate(defaultStartDate(totalWeeks));
  }, [brief?.id]);

  async function loadBenchmarks(id) {
    try {
      const rows = await api.getBrandHealthBenchmarks(id);
      setBm(Object.fromEntries((rows || []).map(r => [r.metric, r])));
    } catch {}
  }

  async function pickBrand(b) {
    setBrand(b);
    setSavedHealth(null);
    setLatestSnapshot(null);
    if (!b.category) { setCatDraft(''); setView('category'); return; }
    await loadBenchmarks(b.id);
    setView('brand_home');
  }

  async function saveCategory() {
    if (!catDraft) return;
    setSavingCat(true);
    try {
      await api.updateBrand(brand.id, { category: catDraft });
      const updated = { ...brand, category: catDraft };
      setBrand(updated);
      await loadBenchmarks(updated.id);
      setView('brand_home');
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setSavingCat(false);
    }
  }

  // Phase 1 pre-fill research: only verified findings (name present, unverified
  // not true) auto-fill a competitor row — unverified ones are surfaced as a
  // separate note in the card instead (never silently promoted into form state).
  async function runPrefillResearch() {
    setPrefillStatus('loading');
    try {
      const result = await api.getPrefillResearch(brand.id);
      setPrefillResearch(result);
      const findings = result?.findings || [];
      const verified = findings.filter(f => f.name && !f.unverified);
      if (verified.length > 0) {
        setForm(p => ({
          ...p,
          competitors: verified.slice(0, 3).map(f => ({
            name: f.name,
            share: f.share != null ? String(f.share) : '',
            awareness: f.awareness != null ? String(f.awareness) : '',
            consumption: '',
          })),
        }));
      }
      setPrefillStatus('done');
    } catch (e) {
      showToast(e.message || 'Research failed', 'danger');
      setPrefillStatus('dismissed');
    }
  }

  async function saveHealth() {
    setSaving(true);
    try {
      const payload = buildPayload(form);
      // Carry the Phase 1 pre-fill research forward onto the snapshot for Phase 2 reuse.
      if (prefillResearch) payload.prefill_research = prefillResearch;
      const result = await api.createBrandHealth(brand.id, payload);
      setSavedHealth(result);
      setView('brand_home');
      showToast('Snapshot saved.', 'success');
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setSaving(false);
    }
  }

  async function generateDiagnosis(healthId) {
    setBriefLoading(true);
    try {
      const result = await api.createStrategicBrief(brand.id, healthId ? { brand_health_id: healthId } : {});
      setBrief(result);
      setSecondaryOpen(false);
      setView('brief');
    } catch (e) {
      if (e.message?.includes('not found') || e.message?.includes('404')) {
        const fresh = await api.getLatestBrandHealth(brand.id);
        setLatestSnapshot(fresh?.id ? fresh : null);
        setSavedHealth(null);
      }
      showToast(e.message || 'Diagnosis failed', 'danger');
    } finally {
      setBriefLoading(false);
    }
  }

  async function handleGenerateNarrative(force = false) {
    setNarrativeLoading(true);
    setNarrativeErr(null);
    try {
      const result = await api.generateNarrative(brand.id, brief.id, force);
      setBrief(b => ({ ...b, narrative_text: result.narrative_text }));
    } catch (e) {
      setNarrativeErr(e.message || 'Narrative generation failed');
    } finally {
      setNarrativeLoading(false);
    }
  }

  async function handleGenerateResearch() {
    setResearchLoading(true);
    try {
      const result = await api.generateMarketResearch(brand.id, brief.id);
      setBrief(b => ({
        ...b,
        market_research: result.market_research,
        market_research_generated_at: result.market_research_generated_at,
        field_suggestions: result.field_suggestions,
      }));
    } catch (e) {
      showToast(e.message || 'Market research failed', 'danger');
    } finally {
      setResearchLoading(false);
    }
  }

  // Phase 3: which wizard step a flaggable field lives on — used by the
  // suggestions card's "Edit snapshot" link. Deep-linking to the exact input
  // isn't supported by the wizard, so landing on the right step is the
  // accepted fallback (per spec).
  function handleEditSuggestion(field) {
    const bh = latestSnapshot || savedHealth;
    if (bh) setForm(populateForm(bh));
    setPrefillStatus('idle');
    setPrefillResearch(null);
    setView(SUGGESTION_FIELD_STEP[field] || 'A');
  }

  async function openBriefFromHome(briefId) {
    try {
      const full = await api.getStrategicBrief(brand.id, briefId);
      setBrief(full);
      setSecondaryOpen(false);
      setView('brief');
    } catch (e) {
      showToast(e.message, 'danger');
    }
  }

  // overrideOpt/overrideStartDate/overrideBudget: used by the R20 recovery-flight card,
  // which bypasses the 3-option selector/selectedOption state entirely (single flight,
  // start date always "today"). Omit all three for the standard selector-driven flow.
  async function createPlanFromBrief(overrideOpt, overrideStartDate, overrideBudget) {
    if (!brief || !brand) return;
    setCreatingPlan(true);
    try {
      const pd = brief.primary_diagnosis || {};
      const objective = normalizeObjective(pd.planiq_objective);
      let opt, effectiveStartDate, totalBudget;
      if (overrideOpt) {
        opt = overrideOpt;
        effectiveStartDate = overrideStartDate;
        totalBudget = overrideBudget;
      } else {
        const ruleType = getRuleType(pd.rule_id);
        const optionSet = FLIGHT_PLANS[ruleType] || FLIGHT_PLANS.continuous;
        opt = optionSet[selectedOption] || optionSet[1];
        effectiveStartDate = startDate;
        const annualBudgetOverride = latestSnapshot?.annual_budget != null ? latestSnapshot.annual_budget : null;
        totalBudget = optionTotals(opt, objective, annualBudgetOverride).totalBudget;
      }
      const { configs: flightConfigs, clamped } = buildFlightConfigs(opt, effectiveStartDate, objective, totalBudget);

      const monthYear = new Date(effectiveStartDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
      const planName = `${brand.name} — ${pd.rule_id} — ${monthYear}`;
      const plan = await api.createPlan({
        brand_id: brand.id,
        name: planName,
        year: new Date(effectiveStartDate).getFullYear(),
        data: {},
      });
      sessionStorage.setItem(`brief_flights_${plan.id}`, JSON.stringify(flightConfigs));
      if (clamped) {
        showToast('Flight adjusted to fit within the plan year — consider a January 1 start date for the full recommended duration.', 'warning');
      }
      navigate(`/plans/${plan.id}?from_brief=${brief.id}`);
    } catch (e) {
      showToast(e.message || 'Failed to create plan', 'danger');
    } finally {
      setCreatingPlan(false);
    }
  }

  async function handleDeleteBrief(briefId) {
    try {
      await api.deleteStrategicBrief(brand.id, briefId);
      setHistory(prev => prev.filter(h => h.id !== briefId));
      setConfirmDeleteId(null);
      const fresh = await api.getLatestBrandHealth(brand.id);
      setLatestSnapshot(fresh?.id ? fresh : null);
      if (brief?.id === briefId) {
        setBrief(null);
        setView('brand_home');
        showToast('Diagnosis deleted.', 'danger');
      }
    } catch (e) {
      showToast(e.message || 'Delete failed', 'danger');
      setConfirmDeleteId(null);
    }
  }

  // ── Views ─────────────────────────────────────────────────────────────────

  if (view === 'loading') {
    return <div style={C.page}><div style={C.empty}>Loading…</div></div>;
  }

  // ── Brand picker ──────────────────────────────────────────────────────────
  if (view === 'pick') {
    const filtered = brands.filter(b => b.name.toLowerCase().includes(search.toLowerCase()));
    return (
      <div style={C.page}>
        <PageHeader />
        <p style={{ color: '#4a6a8a', fontSize: 14, marginTop: -12, marginBottom: 20 }}>
          Select a brand to run a health check and generate a strategic recommendation.
        </p>
        <input placeholder="Search brands…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...C.input, maxWidth: 280, marginBottom: 20 }} />
        {brands.length === 0
          ? <div style={C.empty}>No brands found.</div>
          : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, maxWidth: 800 }}>
              {filtered.map(b => (
                <button key={b.id} onClick={() => pickBrand(b)} style={{
                  background: 'rgba(255,255,255,0.82)', border: '1px solid #dce8f0',
                  borderRadius: 10, padding: '16px 18px', cursor: 'pointer', textAlign: 'left',
                  transition: 'box-shadow .15s, border-color .15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 2px 12px rgba(0,144,208,0.12)`; e.currentTarget.style.borderColor = `${ACCENT}60`; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = '#dce8f0'; }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1a2a3a', marginBottom: 4 }}>{b.name}</div>
                  {b.category
                    ? <span style={C.mutedLabel()}>{catLabel(b.category)}</span>
                    : <span style={{ ...C.mutedLabel(), color: '#d08000' }}>Category not set</span>}
                </button>
              ))}
            </div>
          )}
      </div>
    );
  }

  // ── Category step ─────────────────────────────────────────────────────────
  if (view === 'category') {
    return (
      <div style={C.page}>
        <PageHeader />
        <div style={card}>
          <button style={backBtn} onClick={() => { setBrand(null); setView('pick'); }}>
            <ArrowLeft size={13} /> Back to brands
          </button>
          <h2 style={{ margin: '16px 0 6px', fontSize: 17, fontWeight: 800, color: '#1a2a3a' }}>{brand?.name}</h2>
          <p style={{ margin: '0 0 24px', color: '#6a8aaa', fontSize: 13 }}>
            Before we start, what category does this brand operate in? This drives benchmark lookups and rule triggers.
          </p>
          <div style={{ marginBottom: 24 }}>
            <label style={C.label}>Brand Category *</label>
            <select value={catDraft} onChange={e => setCatDraft(e.target.value)}
              style={{ ...C.input, cursor: 'pointer' }}>
              <option value="">— Select category —</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <button style={{ ...C.btnPrimary, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onClick={saveCategory} disabled={!catDraft || savingCat}>
            {savingCat ? 'Saving…' : <><span>Save & Start Diagnostic</span><ChevronRight size={15} /></>}
          </button>
        </div>
      </div>
    );
  }

  // ── Wizard steps A–D ──────────────────────────────────────────────────────
  if (STEPS.includes(view)) {
    const category = brand?.category;
    const isFinal  = view === 'D';
    const promoIncomplete = form.business_moment === 'promo' && (
      !(form.promo_context?.is_repetitive === 'true' || form.promo_context?.is_repetitive === 'false')
      || !form.promo_context?.campaign_start_date
      || !form.promo_context?.campaign_end_date
    );

    const stepContent = {
      A: (
        <>
          <SelectField label="Business Moment" field="business_moment" form={form} setForm={setForm} options={MOMENTS} />
          {form.business_moment === 'promo' && (
            <div style={promoSectionStyle}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2a3a', marginBottom: 14 }}>
                Tell us about this promotion
              </div>
              <PromoSelect
                label="Is this a repeating promo, or new this year?"
                value={form.promo_context?.is_repetitive}
                onChange={v => setPromoField(setForm, 'is_repetitive', v)}
                options={[
                  { value: 'true', label: 'Repeating promo' },
                  { value: 'false', label: 'New this year' },
                ]}
              />
              {form.promo_context?.is_repetitive === 'true' && (
                <>
                  <PromoNumberInput
                    label="Years Running"
                    value={form.promo_context?.years_running}
                    onChange={v => setPromoField(setForm, 'years_running', v)}
                    help="How many years has this mechanic run, including this one?"
                  />
                  <PromoSelect
                    label="Has the Mechanic Changed?"
                    value={form.promo_context?.mechanic_changed}
                    onChange={v => setPromoField(setForm, 'mechanic_changed', v)}
                    options={[
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]}
                  />
                  {form.promo_context?.mechanic_changed === 'true' && (
                    <PromoTextArea
                      label="What Changed?"
                      value={form.promo_context?.mechanic_change_note}
                      onChange={v => setPromoField(setForm, 'mechanic_change_note', v)}
                      placeholder="e.g. bigger prizes, new partner, different eligibility"
                    />
                  )}
                  <PromoPctInput
                    label="Prior Year Participation Rate"
                    value={form.promo_context?.prior_year_participation_rate}
                    onChange={v => setPromoField(setForm, 'prior_year_participation_rate', v)}
                  />
                  <PromoPctInput
                    label="Current Participation Rate"
                    value={form.promo_context?.current_participation_rate}
                    onChange={v => setPromoField(setForm, 'current_participation_rate', v)}
                  />
                  <PromoPctInput
                    label="Participation Decline"
                    value={form.promo_context?.participation_decline_pct}
                    onChange={v => setPromoField(setForm, 'participation_decline_pct', v)}
                    help="If you don't have exact rates, estimate the decline directly — e.g. 30"
                  />
                  <PromoSelect
                    label="Media Weight vs. Last Year"
                    value={form.promo_context?.media_weight_vs_prior}
                    onChange={v => setPromoField(setForm, 'media_weight_vs_prior', v)}
                    options={[
                      { value: 'increased', label: 'Increased' },
                      { value: 'same', label: 'Same' },
                      { value: 'decreased', label: 'Decreased' },
                      { value: 'not_sure', label: 'Not sure' },
                    ]}
                  />
                </>
              )}
              {form.promo_context?.is_repetitive === 'false' && (
                <>
                  <PromoPctInput
                    label="Expected Participation Rate"
                    value={form.promo_context?.expected_participation_rate}
                    onChange={v => setPromoField(setForm, 'expected_participation_rate', v)}
                  />
                  <PromoPctInput
                    label="Current Participation Rate"
                    value={form.promo_context?.current_participation_rate}
                    onChange={v => setPromoField(setForm, 'current_participation_rate', v)}
                  />
                </>
              )}
              <PromoDateInput
                label="Campaign Start Date"
                value={form.promo_context?.campaign_start_date}
                onChange={v => setPromoField(setForm, 'campaign_start_date', v)}
                required
              />
              <PromoDateInput
                label="Campaign End Date"
                value={form.promo_context?.campaign_end_date}
                onChange={v => setPromoField(setForm, 'campaign_end_date', v)}
                required
              />
            </div>
          )}
          <PctInput label="Aided Awareness" field="aided_awareness" form={form} setForm={setForm}
            help="'Have you heard of this brand?' — the most commonly available metric" bm={bm} category={category}
            nudge={form.aided_awareness !== '' && parseFloat(form.aided_awareness) > 80
              ? 'If your aided awareness is above 80%, adding unaided awareness in Step C will significantly sharpen your diagnosis.'
              : null} />
          <PctInput label="Brand Preference" field="preference" form={form} setForm={setForm}
            help="'If choosing today, which brand would you pick?'" bm={bm} category={category} />
        </>
      ),
      B: (
        <>
          {prefillStatus !== 'dismissed' && (
            <div style={{ ...C.feedback('info'), marginBottom: 20 }}>
              {prefillStatus === 'idle' && (
                <>
                  <div style={{ marginBottom: 10 }}>Run market research now to pre-fill known competitors?</div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" style={C.btnPrimary} onClick={runPrefillResearch}>Run research</button>
                    <button type="button" style={C.btnSecondary} onClick={() => setPrefillStatus('dismissed')}>Skip, I'll enter manually</button>
                  </div>
                </>
              )}
              {prefillStatus === 'loading' && <div>Researching competitors…</div>}
              {prefillStatus === 'done' && (() => {
                const findings = prefillResearch?.findings || [];
                const verified = findings.filter(f => f.name && !f.unverified);
                const unverified = findings.filter(f => f.name && f.unverified);
                return (
                  <div>
                    <div style={{ marginBottom: unverified.length ? 6 : 0 }}>
                      {verified.length > 0
                        ? `Pre-filled ${verified.length} competitor${verified.length === 1 ? '' : 's'} from research — review in Step C.`
                        : 'Research ran but found no verifiable named competitors — enter manually in Step C.'}
                    </div>
                    {unverified.length > 0 && (
                      <div style={{ fontSize: 12, color: '#8aa0b8' }}>
                        Research also mentioned but couldn't verify: {unverified.map(f => f.name).join(', ')}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          <SelectField label="Sales Trend (YoY)" field="sales_trend" form={form} setForm={setForm} options={TRENDS} />
          <SelectField label="Category Trend (YoY)" field="category_trend" form={form} setForm={setForm} options={TRENDS} />
          <PctInput label="Market Share" field="market_share" form={form} setForm={setForm} bm={bm} category={category} />
          <PctInput label="Loyalty / Repeat Purchase" field="loyalty" form={form} setForm={setForm} bm={bm} category={category} />
        </>
      ),
      C: (
        <>
          <PctInput label="Unaided Awareness" field="unaided_awareness" form={form} setForm={setForm}
            help="Name brands in this category unprompted — often unavailable except for brands running their own surveys"
            bm={bm} category={category} />
          <div style={{ marginBottom: 20 }}>
            {(form.competitors || []).map((comp, idx) => {
              const isMain = idx === 0;
              const rowLabel = isMain ? 'Main Competitor' : `Competitor ${idx + 1}`;
              return (
                <div key={idx} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <label style={C.label}>{rowLabel}</label>
                    {!isMain && (
                      <button type="button"
                        onClick={() => setForm(p => ({ ...p, competitors: p.competitors.filter((_, i) => i !== idx) }))}
                        style={{ background: 'none', border: 'none', color: '#8aa0b8', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>
                        ×
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <input
                      type="text"
                      placeholder="Competitor name"
                      value={comp.name}
                      onChange={e => setForm(p => {
                        const updated = [...p.competitors];
                        updated[idx] = { ...updated[idx], name: e.target.value };
                        return { ...p, competitors: updated };
                      })}
                      style={{ ...C.input, flex: 2, minWidth: 0, marginBottom: 0 }}
                    />
                    <input
                      type="number" min="0" max="100" step="0.1"
                      placeholder="Aware %"
                      value={comp.awareness}
                      onChange={e => setForm(p => {
                        const updated = [...p.competitors];
                        updated[idx] = { ...updated[idx], awareness: e.target.value };
                        return { ...p, competitors: updated };
                      })}
                      style={{ ...C.input, flex: 1, minWidth: 0, marginBottom: 0 }}
                    />
                    <input
                      type="number" min="0" max="100" step="0.1"
                      placeholder="Share %"
                      value={comp.share}
                      onChange={e => setForm(p => {
                        const updated = [...p.competitors];
                        updated[idx] = { ...updated[idx], share: e.target.value };
                        return { ...p, competitors: updated };
                      })}
                      style={{ ...C.input, flex: 1, minWidth: 0, marginBottom: 0 }}
                    />
                    <input
                      type="number" min="0" max="100" step="0.1"
                      placeholder="Use %"
                      value={comp.consumption}
                      onChange={e => setForm(p => {
                        const updated = [...p.competitors];
                        updated[idx] = { ...updated[idx], consumption: e.target.value };
                        return { ...p, competitors: updated };
                      })}
                      style={{ ...C.input, flex: 1, minWidth: 0, marginBottom: 0 }}
                    />
                  </div>
                </div>
              );
            })}
            {(form.competitors || []).length < 3 && (
              <button
                type="button"
                onClick={() => setForm(p => ({ ...p, competitors: [...(p.competitors || []), emptyCompetitor()] }))}
                style={{ ...C.btnSecondary, fontSize: 13, padding: '5px 12px' }}
              >+ Add competitor</button>
            )}
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={C.label}>Anything else we should know before researching the market?</label>
            <textarea
              value={form.market_context_notes || ''}
              onChange={e => setForm(p => ({ ...p, market_context_notes: e.target.value }))}
              placeholder="e.g. name the competitor you're most concerned about and why, flag any recent market changes we should focus on, or note anything specific to your category or market that general research might miss."
              rows={3}
              style={{ ...C.input, resize: 'vertical', fontFamily: 'inherit', height: 'auto' }}
            />
          </div>
        </>
      ),
      D: (
        <>
          <PctInput label="Trial" field="trial" form={form} setForm={setForm} bm={bm} category={category} />
          <PctInput label="Consumption" field="consumption" form={form} setForm={setForm}
            help="What % of people currently use/buy this brand — pairs with aided awareness to compute a funnel-efficiency ratio"
            bm={bm} category={category} />
          {DIST_CATS.has(category) && (
            <PctInput label="Distribution" field="distribution" form={form} setForm={setForm} bm={bm} category={category} />
          )}
          <div style={{ marginBottom: 20 }}>
            <label style={C.label}>Annual Media Budget (€)</label>
            <input type="number" min="0" step="1000" value={form.annual_budget}
              onChange={e => setForm(p => ({ ...p, annual_budget: e.target.value }))}
              placeholder="e.g. 150000" style={C.input} />
          </div>
        </>
      ),
    }[view];

    return (
      <div style={C.page}>
        <PageHeader />
        <div style={card}>
          {/* Brand chip + change link */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#1a2a3a' }}>{brand?.name}</span>
              {category && <span style={{ ...C.mutedLabel(), marginLeft: 8 }}>{catLabel(category)}</span>}
            </div>
            <button style={backBtn} onClick={() => setView('brand_home')}>
              <ArrowLeft size={13} /> Back
            </button>
          </div>

          <StepDots current={view} />

          <h3 style={{ margin: '0 0 20px', fontSize: 14, fontWeight: 700, color: '#1a2a3a' }}>
            {STEP_SUBTITLE[view]}
          </h3>

          {stepContent}

          {promoIncomplete && (
            <div style={{ ...C.feedback('warning'), marginBottom: 12 }}>
              Complete the promotion details above (repeating/new + campaign start and end dates) to continue.
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button style={{ ...C.btnSecondary, flex: 1 }}
              onClick={saveHealth} disabled={saving || promoIncomplete}>
              {saving ? 'Saving…' : 'Get my diagnosis now'}
            </button>
            <button
              style={{ ...C.btnPrimary, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              onClick={isFinal ? saveHealth : () => setView(STEP_NEXT[view])}
              disabled={saving || promoIncomplete}>
              {isFinal
                ? (saving ? 'Saving…' : 'Get my diagnosis')
                : <><span>Add more details</span><ChevronRight size={15} /></>}
            </button>
          </div>

          {!isFinal && (
            <p style={{ ...muted, textAlign: 'center', marginTop: 12 }}>
              That's enough for an initial diagnosis — or add more for a sharper recommendation.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Brand home ───────────────────────────────────────────────────────────
  if (view === 'brand_home') {
    // pendingSnap: prefer DB-fetched latestSnapshot; fall back to in-memory savedHealth
    const pendingSnap = latestSnapshot || savedHealth;
    const hasPending  = pendingSnap && !history.some(h => h.brand_health_id === pendingSnap.id);
    // A diagnosis already exists for the latest snapshot (nothing pending) —
    // give the user a clear, prominent way to go straight back to it, instead
    // of only "New Diagnostic →" (destructive, wipes the wizard) and the
    // history table's small "Load" link (easy to miss, reads as an archive
    // action). Two real incidents confirmed users treat "New Diagnostic" as
    // "show me my thing" when it should only ever mean "start completely
    // fresh" — this state exists specifically to give them the correct button.
    const hasLatestBrief = !hasPending && history.length > 0;

    // Build a short human-readable summary of the snapshot
    const MOMENT_LABEL = Object.fromEntries(MOMENTS.map(m => [m.value, m.label]));
    const snapParts = [];
    if (pendingSnap?.business_moment) snapParts.push(MOMENT_LABEL[pendingSnap.business_moment] || pendingSnap.business_moment);
    if (pendingSnap?.aided_awareness != null) snapParts.push(`Aided awareness ${pendingSnap.aided_awareness}%`);
    const firstComp = pendingSnap?.competitors?.[0]?.name;
    if (firstComp) snapParts.push(`Main competitor: ${firstComp}`);
    const snapSummary = snapParts.join(' · ');

    return (
      <div style={C.page}>
        <PageHeader />
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#1a2a3a' }}>{brand?.name}</span>
              {brand?.category && <span style={C.mutedLabel()}>{catLabel(brand.category)}</span>}
            </div>
            <button style={backBtn} onClick={() => { setBrand(null); setSavedHealth(null); setLatestSnapshot(null); setView('pick'); }}>
              <ArrowLeft size={13} /> Change brand
            </button>
          </div>

          {/* Past diagnostics card */}
          <div style={{ background: 'rgba(255,255,255,0.88)', border: '1px solid #dce8f0', borderRadius: 14, padding: '24px 28px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1a2a3a', marginBottom: 16 }}>Past Diagnostics</div>

            {/* Undiagnosed snapshot strip */}
            {hasPending && (
              <div style={{
                ...C.feedback('info'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 14,
                marginBottom: 16,
                flexWrap: 'wrap',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1a2a3a', marginBottom: 2 }}>
                    Snapshot from {fmtDate(pendingSnap.created_at)} — not yet diagnosed
                  </div>
                  {snapSummary && (
                    <div style={{ fontSize: 12, color: '#4a6a8a' }}>{snapSummary}</div>
                  )}
                </div>
                <button
                  style={{ ...C.btnPrimary, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => generateDiagnosis(pendingSnap.id)}
                  disabled={briefLoading}>
                  {briefLoading ? 'Analysing…' : <><Activity size={14} /> Generate diagnosis <ChevronRight size={13} /></>}
                </button>
              </div>
            )}

            {historyLoading ? (
              <div style={{ color: '#8aa0b8', fontSize: 13, padding: '8px 0' }}>Loading…</div>
            ) : history.length === 0 ? (
              <div style={C.empty}>No diagnostics yet for this brand.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} style={{ borderBottom: '1px solid #eef4f8' }}>
                      {confirmDeleteId === h.id ? (
                        <td colSpan={3} style={{ ...C.td, paddingTop: 10, paddingBottom: 10 }}>
                          <span style={{ fontSize: 13, color: '#4a6a8a' }}>Delete this diagnosis? Cannot be undone.</span>
                          <button style={{ ...textBtn, color: '#d04040', marginLeft: 12 }}
                            onClick={() => handleDeleteBrief(h.id)}>Confirm</button>
                          <button style={{ ...textBtn, marginLeft: 8 }}
                            onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                        </td>
                      ) : (
                        <>
                          <td style={{ ...C.td, color: '#4a6a8a', fontSize: 13, width: '40%' }}>{fmtDate(h.created_at)}</td>
                          <td style={C.td}>
                            {h.primary_diagnosis?.rule_id
                              ? <span style={C.mutedLabel()}>{h.primary_diagnosis.rule_id}</span>
                              : <span style={{ color: '#8aa0b8', fontSize: 12 }}>—</span>}
                          </td>
                          <td style={{ ...C.td, textAlign: 'right' }}>
                            <button style={textBtn} onClick={() => openBriefFromHome(h.id)}>Load</button>
                            <button style={{ ...textBtn, color: '#d04040', marginLeft: 10 }}
                              onClick={() => setConfirmDeleteId(h.id)}>Delete</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {hasLatestBrief && (
              <button
                style={{ ...C.btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => openBriefFromHome(history[0].id)}>
                <Activity size={14} /> View Latest Diagnosis <ChevronRight size={14} />
              </button>
            )}
            <button
              style={hasLatestBrief
                ? { ...C.btnSecondary, display: 'flex', alignItems: 'center', gap: 6 }
                : { ...C.btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={() => { setForm(emptyForm()); setSavedHealth(null); setPrefillStatus('idle'); setPrefillResearch(null); setView('A'); }}>
              New Diagnostic <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Brief view ────────────────────────────────────────────────────────────
  if (view === 'brief' && brief) {
    const pd           = brief.primary_diagnosis || {};
    const secondary    = brief.secondary_observations || [];
    const addCtx       = brief.additional_context || [];
    const narSections  = parseNarrative(brief.narrative_text);
    const rd           = brief.market_research;
    const isLegacy     = !!(rd && rd.raw_text && !rd.findings);
    const findings     = rd?.findings || [];
    const fm           = groupFindings(findings);

    const cardHdr = (title, right) => (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #dce8f0' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#1a2a3a' }}>{title}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{right}</div>
      </div>
    );

    const findingItem = (f, i) => f.unverified ? (
      <div key={i} style={{
        marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8,
        padding: '8px 10px', borderRadius: 6, border: '1px dashed #d08000', background: 'rgba(208,128,0,0.05)',
      }}>
        <AlertTriangle size={13} color="#d08000" style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontSize: 13, color: '#1a2a3a', lineHeight: 1.55 }}>{f.text}</div>
          <div style={{ fontSize: 11, color: '#d08000', marginTop: 3, fontWeight: 600 }}>
            ⚠ could not verify against search results
          </div>
          <div style={{ fontSize: 11, color: '#8aa0b8', marginTop: 2 }}>
            {f.source}{f.date ? ` · ${f.date}` : ''}
          </div>
        </div>
      </div>
    ) : (
      <div key={i} style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT, flexShrink: 0, marginTop: 6 }} />
        <div>
          <div style={{ fontSize: 13, color: '#1a2a3a', lineHeight: 1.55 }}>{f.text}</div>
          <div style={{ fontSize: 11, color: '#8aa0b8', marginTop: 3 }}>
            {f.source}{f.date ? ` · ${f.date}` : ''}
          </div>
        </div>
      </div>
    );

    return (
      <div style={C.page}>
        <PageHeader />

        {/* Brand header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 720, margin: '0 auto 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#1a2a3a' }}>{brand?.name}</span>
            {brand?.category && <span style={C.mutedLabel()}>{catLabel(brand.category)}</span>}
            <span style={{ ...C.mutedLabel(), color: '#2a8a5a' }}>✓ Saved</span>
          </div>
          <button style={backBtn} onClick={() => { setBrief(null); setView('brand_home'); }}>
            <ArrowLeft size={13} /> Back
          </button>
        </div>

        {/* Card 1 — Strategic Diagnosis */}
        <div style={{ ...briefCard, marginBottom: 16 }}>
          {cardHdr('Strategic Diagnosis', <span style={C.mutedLabel()}>{pd.rule_id}</span>)}

          {!pd.rule_id ? (
            <div style={C.feedback('info')}>
              No diagnosis generated — the data entered didn't trigger any rules. Try adding aided awareness below 50%, or add preference, market share, or sales trend data.
            </div>
          ) : (
            <>
              <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.65, color: '#1a2a3a' }}>
                {pd.diagnosis_text}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div>
                  <div style={C.label}>Strategic Objective</div>
                  <div style={{ fontSize: 13, color: '#1a2a3a', marginTop: 4 }}>{pd.strategic_objective}</div>
                </div>
                <div>
                  <div style={C.label}>PlanIQ Objective</div>
                  <div style={{ fontSize: 13, color: '#1a2a3a', marginTop: 4 }}>{pd.planiq_objective}</div>
                </div>
              </div>
            </>
          )}

          {pd.recommended_kpis?.length > 0 && (
            <div style={{ marginBottom: secondary.length > 0 || addCtx.length > 0 ? 20 : 0 }}>
              <div style={C.label}>Recommended KPIs</div>
              <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none' }}>
                {pd.recommended_kpis.map(kpi => (
                  <li key={kpi} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13, color: '#1a2a3a' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT, flexShrink: 0 }} />
                    {KPI_LABEL[kpi] || kpi}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {secondary.length > 0 && (
            <div style={{ borderTop: '1px solid #eef4f8', paddingTop: 14, marginTop: 4 }}>
              <button
                onClick={() => setSecondaryOpen(o => !o)}
                style={{ ...backBtn, color: ACCENT, fontWeight: 600, fontSize: 13, gap: 5 }}>
                {secondaryOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                Secondary signals ({secondary.length})
              </button>
              {secondaryOpen && (
                <div style={{ marginTop: 12 }}>
                  {secondary.map((obs, i) => (
                    <div key={i} style={{ padding: '10px 0', borderBottom: i < secondary.length - 1 ? '1px solid #eef4f8' : 'none' }}>
                      <span style={{ ...C.mutedLabel(), display: 'inline-block', marginBottom: 4 }}>{obs.rule_id}</span>
                      <div style={{ fontSize: 13, color: '#4a6a8a', lineHeight: 1.5 }}>{obs.diagnosis_text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {addCtx.length > 0 && (
            <div style={{ borderTop: '1px solid #eef4f8', marginTop: 12, paddingTop: 10 }}>
              {addCtx.map((ctx, i) => (
                <div key={i} style={{ fontSize: 12, color: '#8aa0b8', fontStyle: 'italic' }}>
                  Also noted: {ctx.rule_id}{ctx.diagnosis_text ? ` — ${ctx.diagnosis_text}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Card 2 — Strategic Brief */}
        <div style={{ ...briefCard, marginBottom: 16 }}>
          {cardHdr('Strategic Brief', <>
            {brief.narrative_text && (
              <button style={{ ...textBtn, display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={() => handleGenerateNarrative(true)} disabled={narrativeLoading}>
                <RefreshCw size={11} /> Regenerate
              </button>
            )}
            <span style={C.mutedLabel()}>AI-generated</span>
          </>)}

          {narrativeLoading ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#6a8aaa', fontSize: 14 }}>
              Writing strategic brief…
            </div>
          ) : brief.narrative_text ? (
            <div>
              {narSections.map(({ header, content }) => (
                <div key={header || 'main'} style={{ marginBottom: 22 }}>
                  {header && (
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: ACCENT, marginBottom: 8 }}>
                      {header}
                    </div>
                  )}
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.8, color: '#2a3a4a' }}>{content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <button style={{ ...C.btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                onClick={() => handleGenerateNarrative(false)} disabled={narrativeLoading}>
                Generate narrative <ChevronRight size={14} />
              </button>
            </div>
          )}
          {narrativeErr && (
            <div style={{ ...C.feedback('warning'), marginTop: 12 }}>{narrativeErr}</div>
          )}
        </div>

        {/* Card 3 — Market Intelligence */}
        <div style={{ ...researchCard, marginBottom: 28 }}>
          {cardHdr('Market Intelligence', <>
            <span style={C.statusDot('warning')} />
            <span style={{ fontSize: 12, color: '#8aa0b8' }}>Verify before use</span>
          </>)}

          {researchLoading ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#6a8aaa', fontSize: 14 }}>
              Researching market…
            </div>
          ) : isLegacy ? (
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.7, color: '#2a3a4a', whiteSpace: 'pre-wrap' }}>
              {rd.raw_text}
            </p>
          ) : findings.length > 0 ? (
            <div>
              {fm.grouped
                ? fm.groups.map(g => (
                    <div key={g.type} style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: ACCENT, marginBottom: 10 }}>
                        {g.label}
                      </div>
                      {g.findings.map(findingItem)}
                    </div>
                  ))
                : fm.items.map(findingItem)
              }
              {rd.coverage_note && <div style={{ ...C.feedback('info'), marginTop: 8 }}>{rd.coverage_note}</div>}
              <div style={{ marginTop: 14, fontSize: 12, color: '#8aa0b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                Research from {fmtDate(brief.market_research_generated_at)}
                {' · '}<button style={textBtn} onClick={handleGenerateResearch} disabled={researchLoading}>Refresh</button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <button style={{ ...C.btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                onClick={handleGenerateResearch} disabled={researchLoading}>
                Research market context <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Card 3.5 — Field Suggestions (Phase 3, advisory only, never auto-applied) */}
        {Array.isArray(brief.field_suggestions) && brief.field_suggestions.length > 0 && (
          <div style={{ ...briefCard, marginBottom: 28 }}>
            {cardHdr('Research suggests reconsidering', null)}
            {brief.field_suggestions.map((s, i) => {
              const label = s.field === 'competitors' && s.competitor_name
                ? `Competitor: ${s.competitor_name}`
                : (SUGGESTION_FIELD_LABEL[s.field] || s.field);
              return (
                <div key={i} style={{
                  ...C.feedback('warning'),
                  marginBottom: i === brief.field_suggestions.length - 1 ? 0 : 10,
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {label}{s.current_value != null && s.current_value !== '' ? ` (currently: ${s.current_value})` : ''}
                  </div>
                  <div style={{ fontWeight: 400 }}>{s.suggestion_reasoning}</div>
                  <button
                    style={{ ...textBtn, marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    onClick={() => handleEditSuggestion(s.field)}
                  >
                    <Pencil size={12} /> Edit snapshot <ChevronRight size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Card 4 — Build Your Plan */}
        {pd.rule_id && (() => {
          const ruleType = getRuleType(pd.rule_id);
          const objective = normalizeObjective(pd.planiq_objective);
          const annualBudgetOverride = latestSnapshot?.annual_budget != null ? latestSnapshot.annual_budget : null;
          // promo_context is read from the brand's most recent snapshot — if the loaded brief
          // is from an older historical snapshot this may not exactly match (same known
          // imprecision as annualBudgetOverride above, both are "current brand state" reads).
          const promoCtx = latestSnapshot?.promo_context;
          const todayISO = new Date().toISOString().slice(0, 10);
          const showRecoveryCard = ruleType === 'promo_response'
            && promoCtx?.campaign_end_date && promoCtx.campaign_end_date >= todayISO;

          if (showRecoveryCard) {
            const weeksRemaining = Math.max(1, weeksBetween(todayISO, promoCtx.campaign_end_date));
            const recoveryBudget = calcRecoveryBudget(
              weeksRemaining, annualBudgetOverride,
              latestSnapshot?.aided_awareness, latestSnapshot?.distribution
            );
            const recoveryOption = { flights: [{ weeks: weeksRemaining, objOverride: 'conversion' }] };
            return (
              <div style={{ ...briefCard, marginBottom: 16 }}>
                {cardHdr('Build Your Plan')}
                <div style={{
                  borderRadius: 10, padding: '16px 18px', marginBottom: 16,
                  border: `1.5px solid ${ACCENT}`, background: `${ACCENT}0d`,
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2a3a', marginBottom: 4 }}>
                    Recovery flight: {weeksRemaining} week{weeksRemaining === 1 ? '' : 's'} remaining until {fmtDate(promoCtx.campaign_end_date)}
                  </div>
                  <div style={{ fontSize: 12, color: '#4a6a8a' }}>
                    Conversion objective · €{recoveryBudget.toLocaleString()}
                    <span style={{ color: '#8aa0b8', marginLeft: 6 }}>
                      {annualBudgetOverride != null ? 'From your budget input' : 'Urgency-weighted estimate (Moldova market estimate)'}
                    </span>
                  </div>
                </div>
                <button
                  style={{ ...C.btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={() => createPlanFromBrief(recoveryOption, todayISO, recoveryBudget)} disabled={creatingPlan}>
                  {creatingPlan ? 'Creating…' : <>Create Plan <ChevronRight size={14} /></>}
                </button>
              </div>
            );
          }

          const optionSet = FLIGHT_PLANS[ruleType] || FLIGHT_PLANS.continuous;
          return (
            <div style={{ ...briefCard, marginBottom: 16 }}>
              {cardHdr('Build Your Plan')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {[1, 2, 3].map(optId => {
                  const opt = optionSet[optId];
                  if (!opt) return null;
                  const { totalBudget, totalWeeks } = optionTotals(opt, objective, annualBudgetOverride);
                  const selected = selectedOption === optId;
                  return (
                    <button key={optId} type="button"
                      onClick={() => {
                        setSelectedOption(optId);
                        if (!startDateTouched) setStartDate(defaultStartDate(totalWeeks));
                      }}
                      style={{
                        textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '14px 16px',
                        border: `1.5px solid ${selected ? ACCENT : '#dce8f0'}`,
                        background: selected ? `${ACCENT}0d` : 'rgba(255,255,255,0.6)',
                      }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2a3a', marginBottom: 4 }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: '#4a6a8a', marginBottom: 6 }}>{flightSummary(opt, objective)}</div>
                      <div style={{ fontSize: 12, color: ACCENT, fontWeight: 600 }}>
                        €{totalBudget.toLocaleString()}
                        <span style={{ color: '#8aa0b8', fontWeight: 400, marginLeft: 6 }}>
                          {annualBudgetOverride != null ? 'From your budget input' : 'Recommended (Moldova market estimate)'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{ marginBottom: 20, maxWidth: 220 }}>
                <label style={C.label}>Start Date</label>
                <input type="date" value={startDate}
                  onChange={e => { setStartDate(e.target.value); setStartDateTouched(true); }} style={C.input} />
              </div>
              <button
                style={{ ...C.btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                onClick={() => createPlanFromBrief()} disabled={creatingPlan}>
                {creatingPlan ? 'Creating…' : <>Create Plan <ChevronRight size={14} /></>}
              </button>
            </div>
          );
        })()}

        {/* Actions */}
        <div style={{ maxWidth: 720, margin: '0 auto 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button style={C.btnSecondary}
            onClick={() => { setForm(emptyForm()); setSavedHealth(null); setBrief(null); setPrefillStatus('idle'); setPrefillResearch(null); setView('A'); }}>
            New snapshot
          </button>
        </div>
      </div>
    );
  }

  return null;
}
